import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import Img from '../../../components/Img';
import LoginOptionsStep from './LoginOptionsStep';
import SignInStep from './SignInStep';
import MagicLinkNotification from './MagicLinkNotification';

import { getApiAxiosInstance } from '../../../utils/network';
import { responseStatusTypes } from '../../../utils/constants';
import { submitMagicLink } from '../../../api';
import { clearAuthError } from '../../../actions';
import { MagicLinkLoginSteps, getMagicLinkSuccessMessage, magicLinkSuccessTitle, magicLinkErrorMessage, SocialAuthProviders } from './utils';

import styles from '../Login/login.scss';

const MagicLinkLoginFlow = ({ onSignInWithCredentials, onJoinNowClick, isSubmitting, facebookUrl, appleUrl, callbackUrl, context }) => {
    const { SIGN_IN, LOGIN_OPTIONS } = MagicLinkLoginSteps;
    const { FACEBOOK, APPLE } = SocialAuthProviders;
    const dispatch = useDispatch();
    const [email, setEmail] = useState('');
    const [step, setStep] = useState(SIGN_IN);
    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success',
        title: '',
        message: '',
    });

    const handleContinueClick = (values) => {
        dispatch(clearAuthError());
        setEmail(values.email);
        setStep(LOGIN_OPTIONS);
    };
    const handleBackClick = () => {
        dispatch(clearAuthError());
        setStep(SIGN_IN);
    };

    const handleOnSubmitMagicLink = async () => {
        const axiosInstance = getApiAxiosInstance();
        try {
            // Both are omitted rather than sent null: the API treats an absent field as
            // "standard flow", and a present-but-empty one would be a stale-tag warning.
            const optionalFields = {
                ...(callbackUrl ? { callbackUrl } : {}),
                ...(context ? { context } : {}),
            };
            const res = await submitMagicLink(axiosInstance, { email, ...optionalFields });
            if (res.status === responseStatusTypes.OK) {
                setNotification({
                    isOpen: true,
                    type: 'success',
                    title: magicLinkSuccessTitle,
                    message: getMagicLinkSuccessMessage(email),
                });
            }
        } catch (err) {
            setNotification({
                isOpen: true,
                type: 'error',
                title: 'Error',
                message: magicLinkErrorMessage,
            });
        }
    };

    const handleCloseNotification = () => {
        setNotification({
            ...notification,
            isOpen: false,
        });
    };

    const handleJoinNow = () => {
       onJoinNowClick();
    };

    const handleLoginWithSocial = (social) => {
        if (social === FACEBOOK) {
            window.location.href = facebookUrl;
        } else if (social === APPLE) {
            window.location.href = appleUrl;
        }
    };

    const handlePasswordSubmit = (values) => {
        const { password } = values;
        onSignInWithCredentials({ email, password });
    };

    return (
        <>
            <div className={`${styles.magicLinkCard}`}>
                {step === LOGIN_OPTIONS && (
                    <div className={`${styles.backButtonContainer}`}>
                        <button
                            type="button"
                            onClick={handleBackClick}
                            className={styles.backButton}
                            data-testid="magic-link-login.back-btn"
                        >
                            <Img src="/img/v4/icon-back-arrow.svg" data-testid="magic-link-login.back-icon" />
                        </button>
                    </div>
                )}
                {step === SIGN_IN && (
                    <div className={`${styles.magicLinkSignInContent}`}>
                        <SignInStep
                            onContinueClick={handleContinueClick}
                            isSubmitting={isSubmitting}
                            email={email}
                            handleLoginWithSocial={handleLoginWithSocial}
                            facebookUrl={facebookUrl}
                            appleUrl={appleUrl}
                        />
                    </div>
                )}
                {step === LOGIN_OPTIONS && (
                    <div className={`${styles.magicLinkLoginOptionsContent}`}>
                        <LoginOptionsStep
                            isSubmitting={isSubmitting}
                            onPasswordSubmit={handlePasswordSubmit}
                            email={email}
                            onSubmitMagicLink={handleOnSubmitMagicLink}
                        />
                    </div>
                )}
                <div className={`${styles.magicLinkJoinNowContent}`}>
                    <div className={`${styles.divider}`} />
                    <div className={`${styles.text} mt38`} data-testid="magic-link-login.no-account-text">
                        <strong>Don&apos;t have an account?</strong> <span><a className="cursor-pointer text-teal" onClick={handleJoinNow} data-testid="magic-link-login.join-now-link">Join now.</a></span>
                    </div>
                </div>
            </div>
            <MagicLinkNotification
                isOpen={notification.isOpen}
                type={notification.type}
                title={notification.title}
                message={notification.message}
                onClose={handleCloseNotification}
            />
        </>
    );
};

MagicLinkLoginFlow.propTypes = {
    onSignInWithCredentials: PropTypes.func.isRequired,
    onJoinNowClick: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool,
    facebookUrl: PropTypes.string.isRequired,
    appleUrl: PropTypes.string.isRequired,
    callbackUrl: PropTypes.string,
    context: PropTypes.string,
};

MagicLinkLoginFlow.defaultProps = {
    isSubmitting: false,
    callbackUrl: null,
    context: null,
};

export default MagicLinkLoginFlow;
